import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import type { Collection } from 'immutable';

import { payCategoriesListSelector } from 'domain/payCategories';
import { findCatById } from 'domain/payCategories/helpers';
import { PAY_CATEGORY_SYSTEM_TYPES } from 'domain/payCategories/constants';
import { userIdSelector } from 'domain/env/envSelector';

import { isPaymentDeletable, PAYMENT_STATUSES, PAYMENT_TAGS } from 'domain/payments/constants';

import { flowStates } from 'pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/PaymentFlowStates';

interface ApprovalLevel {
  status?: string;
  nodes?: Array<{ status?: string; username?: string }>;
}

interface PaymentApprovals {
  status?: string;
  levels?: ApprovalLevel[];
}

interface PaymentItem {
  approvals?: PaymentApprovals;
  tags: { includes: (tag: string) => boolean };
  paymentStatus?: { value?: string };
}

interface PaymentActionAbility {
  canStartApprovalFlow: boolean;
  canResetApprovalFlow: boolean;
  canPay: boolean;
  canApprove: boolean;
  canStartPaymentProcess: boolean;
  canArchive: boolean;
  canUnarchive: boolean;
  canSaveForLater: boolean;
  canDelete: boolean;
  isVisibleApprovalFlowButtons: boolean;
  isVisibleSendToApprovalButton: boolean;
  isVisibleStartPaymentProcessButton: boolean;
  isVisibleResetApprovalFlowButton: boolean;
  isVisiblePayButton: boolean;
  isVisibleMergeButton: boolean;
  isVisibleSaveForLaterButton: boolean;
  isVisibleArchiveButton: boolean;
  isVisibleUnarchiveButton: boolean;
  isVisibleDeleteButton: boolean;
}

const usePaymentActionAbility = (payments: Collection<unknown, PaymentItem>): PaymentActionAbility => {
  const { search } = useLocation();
  const { category1 } = useParams();
  const categories = useSelector(payCategoriesListSelector);
  const currentUser = useSelector(userIdSelector);

  const flowState = useMemo(() => new URLSearchParams(search).get('flowState'), [search]);

  const currentCategory = useMemo(
    () => (category1 ? findCatById(categories, category1) : null),
    [category1, categories],
  );
  const categoryType = currentCategory?.get('nameId') ?? null;

  const canStartApprovalFlow = useMemo(
    () =>
      payments.some((payment) => {
        const approvals = payment?.approvals;
        return approvals?.status === 'draft' && (approvals?.levels?.some((level) => level?.status === 'draft') ?? false);
      }),
    [payments],
  );

  const canResetApprovalFlow = useMemo(
    () => payments.some((payment) => payment?.approvals?.status === 'active'),
    [payments],
  );

  const canPay = useMemo(
    () =>
      payments.some((payment) => {
        const approvals = payment?.approvals;
        if (approvals?.status === 'draft') {
          return approvals.levels?.every((level) => level?.status === 'inactive') ?? false;
        }
        if (approvals?.status === 'active') {
          return approvals.levels?.every((level) => level?.status === 'approved' || level?.status === 'inactive') ?? false;
        }
        return false;
      }),
    [payments],
  );

  const canApprove = useMemo(
    () =>
      payments.some((payment) => {
        const approvals = payment?.approvals;
        if (approvals?.status !== 'active') return false;
        return (
          approvals.levels?.some(
            (level) =>
              level?.status === 'pending' &&
              (level?.nodes?.some((node) => node?.status === 'pending' && node?.username === currentUser) ?? false),
          ) ?? false
        );
      }),
    [payments, currentUser],
  );

  const canStartPaymentProcess = useMemo(
    () => payments.some((payment) => payment.tags.includes(PAYMENT_TAGS.saveForLater)),
    [payments],
  );

  const canArchive = useMemo(
    () => payments.some((payment) => !payment.tags.includes(PAYMENT_TAGS.archive)),
    [payments],
  );

  const canUnarchive = useMemo(
    () => payments.some((payment) => payment.tags.includes(PAYMENT_TAGS.archive)),
    [payments],
  );

  const canSaveForLater = useMemo(
    () => payments.some((payment) => payment?.paymentStatus?.value === PAYMENT_STATUSES.PREPARATION),
    [payments],
  );

  // DPM-646: `some` (not `every`) so a mixed bulk selection keeps the action enabled — the backend
  // skips the ineligible ones and reports how many were actually deleted.
  const canDelete = useMemo(
    () => payments.some((payment) => isPaymentDeletable(payment?.paymentStatus?.value)),
    [payments],
  );

  const visibility = useMemo(() => {
    const isAllCategory = categoryType === PAY_CATEGORY_SYSTEM_TYPES.all;
    const isAllFlowState = flowState === flowStates.all;
    const isAllFlowStateAndAllCategory = isAllFlowState || isAllCategory;

    const isVisibleApprovalFlowButtons =
      flowState === flowStates.pendingApproval ||
      isAllFlowStateAndAllCategory ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.to_approve;

    const isVisibleSendToApprovalButton =
      flowState === flowStates.paymentPreparation ||
      isAllFlowStateAndAllCategory ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.save_for_later;

    const isVisibleStartPaymentProcessButton =
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.save_for_later || isAllCategory;

    const isVisibleResetApprovalFlowButton =
      isVisibleApprovalFlowButtons ||
      flowState === flowStates.approved ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.approval_rejected;

    const isVisiblePayButton =
      flowState === flowStates.paymentPreparation || flowState === flowStates.approved || isAllFlowStateAndAllCategory;

    const isVisibleMergeButton = flowState === flowStates.paymentPreparation || isAllFlowStateAndAllCategory;

    const isVisibleSaveForLaterButton = flowState === flowStates.paymentPreparation || isAllFlowStateAndAllCategory;

    const isVisibleArchiveButton =
      flowState === flowStates.paymentPreparation ||
      flowState === flowStates.paid ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.save_for_later ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.voided ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.denied ||
      isAllFlowStateAndAllCategory;

    const isVisibleUnarchiveButton = categoryType === PAY_CATEGORY_SYSTEM_TYPES.archive || isAllCategory;

    // every place where deletable (pre-PROCESSING) payments live; archived payments keep their
    // status, so the archive category qualifies too — the status check (canDelete) still gates it
    const isVisibleDeleteButton =
      flowState === flowStates.paymentPreparation ||
      flowState === flowStates.pendingApproval ||
      flowState === flowStates.approved ||
      isAllFlowStateAndAllCategory ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.save_for_later ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.to_approve ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.approval_rejected ||
      categoryType === PAY_CATEGORY_SYSTEM_TYPES.archive;

    return {
      isVisibleApprovalFlowButtons,
      isVisibleSendToApprovalButton,
      isVisibleStartPaymentProcessButton,
      isVisibleResetApprovalFlowButton,
      isVisiblePayButton,
      isVisibleMergeButton,
      isVisibleSaveForLaterButton,
      isVisibleArchiveButton,
      isVisibleUnarchiveButton,
      isVisibleDeleteButton,
    };
  }, [categoryType, flowState]);

  return {
    canStartApprovalFlow,
    canResetApprovalFlow,
    canPay,
    canApprove,
    canStartPaymentProcess,
    canArchive,
    canUnarchive,
    canSaveForLater,
    canDelete,
    ...visibility,
  };
};

export default usePaymentActionAbility;
